import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { colors, fontWeight } from 'seedling/constants';

/**
 * See storybook for example usages.
 * If you need a simple table header with just strings, pass the `headers` prop.
 * If you need more complex content in the header, pass a `headerComponent` instead.
 */
class CardTable extends React.Component {
  static propTypes = {
    headers: PropTypes.arrayOf(PropTypes.string),
    headerComponent: PropTypes.element,
  };

  static defaultProps = {
    headers: [],
  };

  header() {
    const { headerComponent, headers } = this.props;

    if (headerComponent) return headerComponent;

    if (!headers.length) return null;

    return (
      <thead>
        <tr>
          {headers.map((header, i) => (
            <CardTable.HeaderCell key={i}>{header}</CardTable.HeaderCell>
          ))}
        </tr>
      </thead>
    );
  }

  render() {
    return (
      <CardTableWrapper>
        {this.header()}
        <tbody>{this.props.children}</tbody>
      </CardTableWrapper>
    );
  }
}

const CardTableWrapper = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${props =>
    !props.theme.legacyPadding &&
    css`
      ${CardTable.HeaderCell} {
        padding: 16px 24px 0 0;
      }

      ${CardTable.Row} {
        &:last-child > td {
          padding-bottom: 0;
        }
      }

      ${CardTable.Cell} {
        padding: 20px 24px 20px 0;
      }

      // if there is no table header, apply 24px padding to top
      & > tbody:only-child > tr:first-of-type > td {
        padding-top: 24px;
      }

      // if there is a table header, reduce padding of first row to 20px
      thead + tbody > tr:first-of-type > td {
        padding-top: 20px;
      }
    `}
`;

CardTable.HeaderCell = styled.th`
  ${fontWeight('normal')};
  color: ${colors.mediumGrey};
  font-size: 12px;
  padding: 0 25px 10px 0;
  text-align: left;
  width: ${props => (props.width ? props.width : '20%')};

  ${props =>
    !props.width &&
    `&:first-child { width: 32%; }
     &:last-child { width: 8%; }
    `}
`;

CardTable.Row = styled.tr`
  & + & {
    border-top: 1px solid ${colors.lightGrey};
  }
`;

CardTable.Cell = styled.td`
  font-size: 14px;
  padding: 12px 15px 12px 0;
  vertical-align: top;

  ${fontWeight('normal')};

  &:last-child {
    padding-right: 0;
    text-align: right;
  }
`;

const { HeaderCell, Row, Cell } = CardTable;

CardTable.displayName = 'CardTable';
HeaderCell.displayName = 'CardTable.HeaderCell';
Row.displayName = 'CardTable.Row';
Cell.displayName = 'CardTable.Cell';

export default CardTable;
export { Cell, Row, HeaderCell };
